"use client";

import Image, { StaticImageData } from "next/image";
import React, { useState, useRef } from "react";

interface BannerProps {
  title?: React.ReactNode;
  desc?: string;
  logoSrc: string | StaticImageData;
  logoAlt?: string;
  glowColor?: string;
}

const TechnologyBanner = ({
  title = "",
  desc = "",
  logoSrc,
  logoAlt = "Technology Logo",
  glowColor = "rgba(0, 98, 255, 0.9)",
}: BannerProps) => {
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const { left, top, width, height } =
      containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setMousePos({ x, y });
  };

  return (
    <div className="w-[90%] max-w-[2500px] mx-auto relative min-h-[40vh] lg:min-h-[50vh] flex items-center py-10">
      <div className="grid grid-cols-1 lg:grid-cols-10 w-full items-center gap-6 lg:gap-10">
        {/* Left Side: Content */}
        <div className="lg:col-span-7 flex flex-col">
          {/* Changed: Added responsive text sizes and adjusted leading for mobile */}
          <h1 className="text-black text-4xl sm:text-5xl md:text-6xl lg:text-[length:var(--service-banner-title)] clash-font capitalize leading-[1.2] lg:leading-[1.1]">
            <span>{title}</span>
          </h1>

          {/* Changed: Adjusted margin and text size for mobile description */}
          <p className="text-[#636364] text-base md:text-lg lg:text-[length:var(--banner-para)] max-w-[65ch] mt-4 lg:mt-8">
            {desc}
          </p>
        </div>

        {/* Right Side: Interactive Reveal Logo (Stays hidden on mobile as per your original) */}
        <div className="hidden lg:col-span-3 lg:flex justify-end items-center relative">
          <div
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="relative w-full max-w-[300px] xl:max-w-[480px] cursor-pointer"
          >
            <Image
              src={logoSrc}
              alt={logoAlt}
              width={600}
              height={600}
              className="w-full h-auto object-contain select-none pointer-events-none opacity-50 bg-[#f4f8fc]"
              sizes="(min-width: 1280px) 480px, 300px"
            />

            <div
              className={`absolute inset-0 transition-opacity duration-300 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              style={{
                maskImage: `radial-gradient(circle 120px at ${mousePos.x}% ${mousePos.y}%, black 0%, transparent 100%)`,
                WebkitMaskImage: `radial-gradient(circle 120px at ${mousePos.x}% ${mousePos.y}%, black 0%, transparent 100%)`,
                filter: `hue-rotate(200deg) saturate(150%) drop-shadow(0 0 20px ${glowColor})`,
              }}
            >
              <Image
                src={logoSrc}
                alt={`${logoAlt} Highlight`}
                width={600}
                height={600}
                className="w-full h-auto object-contain select-none pointer-events-none"
                sizes="(min-width: 1280px) 480px, 300px"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologyBanner;
